import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '../types';
import { observeAuthState, refreshAuthToken, auth, convertFirebaseUser } from '../services/firebase';
import * as Sentry from '@sentry/react';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  authError: string | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  authError: null,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let refreshInterval: NodeJS.Timeout;

    const unsubscribe = observeAuthState((authUser) => {
      if (!mounted) return;

      if (authUser) {
        setUser(authUser);
        if (!authUser.isAdmin) {
          setAuthError('You do not have admin permission.');
        } else {
          setAuthError(null);
        }

        // Set Sentry user context
        Sentry.setUser({
          id: authUser.uid,
          email: authUser.email || undefined,
          username: authUser.displayName || authUser.email || undefined,
        });
      } else {
        setUser(null);
        console.log('User is signed out, rendering login screen.');
        
        // Clear Sentry user context on logout
        Sentry.setUser(null);
      }

      setIsLoading(false);
    });

    // Refresh token periodically (every 45 minutes)
    refreshInterval = setInterval(async () => {
      const refreshed = await refreshAuthToken();
      if (!refreshed) {
        console.warn('⚠️ Token refresh failed — logging out.');
        setUser(null);
      } else {
        console.log('🔄 Token refreshed!');
      }
    }, 45 * 60 * 1000); // 45 mins

    // Handle page visibility change to refresh token when user returns
    const handleVisibilityChange = async () => {
      if (!document.hidden && mounted) {
        // Page became visible - refresh token
        console.log('📱 Page visible, refreshing token...');
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          console.log('✅ Token refreshed on page visible');
          // Update the user with the new token
          const currentUser = auth.currentUser;
          if (currentUser) {
            const updatedUser = await convertFirebaseUser(currentUser);
            setUser(updatedUser);
          }
        } else {
          console.warn('⚠️ Failed to refresh token on page visible');
        }
      }
    };

    // Handle online/offline events
    const handleOnline = async () => {
      if (mounted) {
        console.log('🌐 Network reconnected, refreshing token...');
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          console.log('✅ Token refreshed after reconnection');
          // Update the user with the new token
          const currentUser = auth.currentUser;
          if (currentUser) {
            const updatedUser = await convertFirebaseUser(currentUser);
            setUser(updatedUser);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      mounted = false;
      unsubscribe();
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, authError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export {};
